/**
 * Document preview overlay — shows the submitted file (PDF/image) with header, body, and footer.
 * Renders ResubmitCommentModal when admin clicks 再提出を依頼する.
 * Shows a confirmation when reversing an existing approval/rejection.
 * For dual-side uploads, tabs switch between 表面 / 裏面.
 */
import React, { useEffect, useState } from 'react';
import type { ChatFlowDocumentFileRound, ChatFlowDocumentItem } from '../../../../services/chatFlowApi';
import downloadAllIcon from '../../../../assests/icons/downloadAll.png';
import { buildDocumentFileUrl, isPdfExtension, isImageExtension, downloadDocumentFile, safeFileNamePart } from './utils';
import ResubmitCommentModal from './ResubmitCommentModal';
import PdfCanvasViewer from '../../../employee/documents/pdfPreview/PdfCanvasViewer';
import './DocumentPreview.css';
import rejectIcon from '../../../../assests/icons/rejected.png';
import approveIcon from '../../../../assests/icons/approved.png';

type PreviewSide = 'front' | 'back';

export interface DocumentPreviewProps {
  /** Document to preview (from chat flow) */
  doc: ChatFlowDocumentItem;
  /** Optional active round (used when opened from a round-specific folder icon). */
  activeRound?: ChatFlowDocumentFileRound | null;
  /** Employee email (for file URL) */
  employeeEmail: string;
  /** Employee name (for download filename; optional) */
  employeeName?: string;
  /** Invitation ID (for file URL) */
  invitationId: string;
  /** Whether resubmit action is allowed for this preview (usually only latest round). */
  allowResubmit?: boolean;
  /** Hide approve/resubmit footer (archive read-only). */
  readOnly?: boolean;
  /** Called when user closes the preview (×) */
  onClose: () => void;
  /** Whether the resubmit comment modal is open */
  showResubmitModal: boolean;
  /** Current resubmit reason text */
  resubmitReason: string;
  /** Called when reason textarea changes */
  onResubmitReasonChange: (value: string) => void;
  /** Called when user clicks 再提出を依頼する */
  onOpenResubmitModal: () => void;
  /** Called when user closes the resubmit modal (キャンセル or ×) */
  onCloseResubmitModal: () => void;
  /** Called when user clicks 保存 in the resubmit modal */
  onSaveResubmit: () => void;
  /** Called when user clicks 承認する — mark document as approved */
  onApprove: () => void;
}

const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  doc,
  activeRound,
  employeeEmail,
  employeeName,
  invitationId,
  allowResubmit = true,
  readOnly = false,
  onClose,
  showResubmitModal,
  resubmitReason,
  onResubmitReasonChange,
  onOpenResubmitModal,
  onCloseResubmitModal,
  onSaveResubmit,
  onApprove,
}) => {
  /** When reversing approval/rejection: message to show and callback on confirm */
  const [reverseConfirm, setReverseConfirm] = useState<{ message: string; onConfirm: () => void } | null>(null);
  const [activeSide, setActiveSide] = useState<PreviewSide>('front');
  const [fileBlobUrl, setFileBlobUrl] = useState<string | null>(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState(false);

  const docTitle = doc.nameJa ?? doc.name;
  const activeFilePath = activeRound?.filePath ?? doc.filePath;
  const activeFilePathBack = activeRound?.filePathBack ?? doc.filePathBack ?? null;
  const activeSubmissionRound = activeRound?.submissionRound ?? null;
  const hasBothSides = Boolean(activeFilePathBack);

  useEffect(() => {
    setActiveSide('front');
  }, [doc.docCode, activeSubmissionRound, activeFilePathBack]);

  /**
   * Approval state for the previewed file only. When previewing a specific round, do not fall back to
   * document-level aggregate — otherwise a pending round would inherit "approved" and hide 承認する.
   */
  const reviewDecision: boolean | null =
    activeRound != null
      ? activeRound.adminApproved === true
        ? true
        : activeRound.adminApproved === false
          ? false
          : null
      : doc.adminApproved === true
        ? true
        : doc.adminApproved === false
          ? false
          : null;
  const activeAdminComment =
    activeRound != null ? activeRound.adminComment ?? null : doc.adminComment ?? null;

  const fileUrl =
    activeFilePath && employeeEmail && invitationId && activeSubmissionRound != null
      ? buildDocumentFileUrl(employeeEmail, invitationId, doc.docCode, activeSubmissionRound)
      : doc.status === 'submitted' && doc.filePath && employeeEmail && invitationId
        ? buildDocumentFileUrl(employeeEmail, invitationId, doc.docCode)
        : null;

  const backFileUrl =
    activeFilePathBack && employeeEmail && invitationId && activeSubmissionRound != null
      ? buildDocumentFileUrl(employeeEmail, invitationId, doc.docCode, activeSubmissionRound, 'back')
      : activeFilePathBack && doc.status === 'submitted' && employeeEmail && invitationId
        ? buildDocumentFileUrl(employeeEmail, invitationId, doc.docCode, undefined, 'back')
        : null;

  const viewingBack = hasBothSides && activeSide === 'back';
  const displayUrl = viewingBack ? backFileUrl : fileUrl;
  const displayPath = viewingBack ? activeFilePathBack : activeFilePath;

  useEffect(() => {
    if (!displayUrl) {
      setFileBlobUrl(null);
      setFileLoading(false);
      setFileError(false);
      return undefined;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    setFileLoading(true);
    setFileError(false);
    setFileBlobUrl(null);

    void fetch(displayUrl, { credentials: 'include' })
      .then((response) => {
        if (!response.ok) throw new Error('preview failed');
        return response.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setFileBlobUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFileError(true);
      })
      .finally(() => {
        if (!cancelled) setFileLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [displayUrl]);

  const renderFileContent = (url: string, path: string | null) => {
    if (isPdfExtension(path)) {
      return (
        <PdfCanvasViewer
          pdfUrl={url}
          title={docTitle}
          className="document-preview__pdf-canvas"
          renderErrorLabel="書類プレビューを表示できません"
        />
      );
    }
    if (isImageExtension(path)) {
      return <img src={url} alt={docTitle} className="document-preview__img" />;
    }
    return <iframe title={docTitle} src={url} className="document-preview__iframe" />;
  };

  const renderDocumentContent = () => {
    if (!fileUrl || !activeFilePath || !employeeEmail || !invitationId) {
      return <div className="document-preview__placeholder">書類プレビュー</div>;
    }
    if (fileLoading) {
      return <div className="document-preview__placeholder">読み込み中...</div>;
    }
    if (fileError || !fileBlobUrl) {
      return <div className="document-preview__placeholder">書類プレビューを表示できません</div>;
    }
    return renderFileContent(fileBlobUrl, displayPath);
  };

  const handleDownload = async () => {
    if (!fileUrl || !activeFilePath) return;
    const empName = employeeName?.trim() || employeeEmail;
    const docName = doc.nameJa ?? doc.name ?? doc.docCode;
    try {
      if (viewingBack && backFileUrl && activeFilePathBack) {
        const backExt = activeFilePathBack.match(/\.[^.]+$/)?.[0] ?? '';
        await downloadDocumentFile(
          backFileUrl,
          `${safeFileNamePart(empName)}_${safeFileNamePart(docName)}_back${backExt}`
        );
        return;
      }
      const ext = activeFilePath.match(/\.[^.]+$/)?.[0] ?? '';
      await downloadDocumentFile(
        fileUrl,
        `${safeFileNamePart(empName)}_${safeFileNamePart(docName)}${ext}`
      );
    } catch {
      alert('ダウンロードに失敗しました');
    }
  };

  const handleApproveClick = () => {
    if (reviewDecision === false) {
      setReverseConfirm({
        message: 'この書類は却下済みです。承認しますか？',
        onConfirm: () => {
          setReverseConfirm(null);
          onApprove();
        },
      });
    } else {
      onApprove();
    }
  };

  const handleResubmitClick = () => {
    if (reviewDecision === true) {
      setReverseConfirm({
        message: 'この書類は承認済みです。再提出を依頼しますか？',
        onConfirm: () => {
          setReverseConfirm(null);
          onOpenResubmitModal();
        },
      });
    } else {
      onOpenResubmitModal();
    }
  };

  return (
    <div className="document-preview" role="dialog" aria-modal="true" aria-labelledby="document-preview-title">
      <div className="document-preview__backdrop" />
      <div className="document-preview__panel">
        <header className="document-preview__header">
          <h2 id="document-preview-title" className="document-preview__title">
            {activeSubmissionRound != null ? (
              <>
                <span className="document-preview__title-doc">{docTitle}</span>{' '}
                <span
                  className={
                    allowResubmit
                      ? 'document-preview__title-round document-preview__title-round--latest'
                      : 'document-preview__title-round'
                  }
                >
                  {allowResubmit ? '提出物' : '前回の提出物'}（第{activeSubmissionRound}ラウンド）
                </span>
              </>
            ) : (
              docTitle
            )}
          </h2>
          <div className="document-preview__header-actions">
            {reviewDecision === false &&
              allowResubmit &&
              (!activeAdminComment || activeAdminComment.trim() === '') && (
              <img
                src={rejectIcon}
                alt="却下"
                className="document-preview__header-reject-icon"
                aria-hidden
              />
            )}
            <button
              type="button"
              className="document-preview__download-btn"
              aria-label="ダウンロード"
              onClick={handleDownload}
              disabled={!fileUrl || (viewingBack && !backFileUrl)}
            >
              <img
                src={downloadAllIcon}
                alt=""
                className="document-preview__download-icon"
                aria-hidden
              />
              <span>ダウンロード</span>
            </button>
            <button
              type="button"
              className="document-preview__close-btn"
              aria-label="閉じる"
              onClick={onClose}
            >
              ×
            </button>
          </div>
        </header>
        <div className="document-preview__body">
          {hasBothSides && (
            <div className="document-preview__side-tabs" role="tablist" aria-label="表裏の切替">
              <button
                type="button"
                role="tab"
                aria-selected={activeSide === 'front'}
                className={
                  activeSide === 'front'
                    ? 'document-preview__side-tab document-preview__side-tab--active'
                    : 'document-preview__side-tab'
                }
                onClick={() => setActiveSide('front')}
              >
                表面
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeSide === 'back'}
                className={
                  activeSide === 'back'
                    ? 'document-preview__side-tab document-preview__side-tab--active'
                    : 'document-preview__side-tab'
                }
                onClick={() => setActiveSide('back')}
                disabled={!backFileUrl}
              >
                裏面
              </button>
            </div>
          )}
          <div className="document-preview__document-area">
            <span className="document-preview__pagination" aria-hidden>
              {hasBothSides ? (activeSide === 'front' ? '表面' : '裏面') : '1 / 1'}
            </span>
            {renderDocumentContent()}
          </div>
          {reviewDecision === true && (
            <div className="document-preview__round-comment">
              <div className="document-preview__round-comment-inner document-preview__round-comment-inner--with-status-icon">
                <img
                  src={approveIcon}
                  alt="承認"
                  className="document-preview__round-comment-approve-icon"
                  aria-hidden
                />
                <div className="document-preview__round-comment-copy">
                  {activeAdminComment != null && activeAdminComment.trim() !== '' ? (
                    <>
                      <div className="document-preview__round-comment-title">コメント</div>
                      <div className="document-preview__round-comment-text">{activeAdminComment}</div>
                    </>
                  ) : (
                    <div className="document-preview__round-comment-title">承認</div>
                  )}
                </div>
              </div>
            </div>
          )}
          {reviewDecision === false && activeAdminComment != null && activeAdminComment.trim() !== '' && (
            <div className="document-preview__round-comment">
              <div className="document-preview__round-comment-inner document-preview__round-comment-inner--with-status-icon">
                <img
                  src={rejectIcon}
                  alt="却下"
                  className="document-preview__round-comment-reject-icon"
                  aria-hidden
                />
                <div className="document-preview__round-comment-copy">
                  <div className="document-preview__round-comment-title">コメント</div>
                  <div className="document-preview__round-comment-text">{activeAdminComment}</div>
                </div>
              </div>
            </div>
          )}
        </div>
        {!readOnly && (
        <footer className="document-preview__footer">
          <div className="document-preview__footer-actions">
            {(reviewDecision === null || reviewDecision === true) && (
              <button type="button" className="document-preview__resubmit-btn" onClick={handleResubmitClick}>
                再提出を依頼する
              </button>
            )}
            {(reviewDecision === null || reviewDecision === false) && (
              <button type="button" className="document-preview__approve-btn" onClick={handleApproveClick}>
                承認する
              </button>
            )}
          </div>
        </footer>
        )}
      </div>

      <ResubmitCommentModal
        open={showResubmitModal}
        reason={resubmitReason}
        onReasonChange={onResubmitReasonChange}
        onClose={onCloseResubmitModal}
        onSave={onSaveResubmit}
      />

      {reverseConfirm && (
        <div className="document-preview__reverse-confirm" role="dialog" aria-modal="true" aria-labelledby="reverse-confirm-title">
          <div className="document-preview__reverse-confirm-backdrop" onClick={() => setReverseConfirm(null)} aria-hidden />
          <div className="document-preview__reverse-confirm-box">
            <p id="reverse-confirm-title" className="document-preview__reverse-confirm-message">
              {reverseConfirm.message}
            </p>
            <div className="document-preview__reverse-confirm-actions">
              <button
                type="button"
                className="document-preview__reverse-confirm-cancel"
                onClick={() => setReverseConfirm(null)}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="document-preview__reverse-confirm-ok"
                onClick={reverseConfirm.onConfirm}
              >
                はい
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentPreview;
